/**
 * Pure helpers for the crew offline queue (offline/queue.ts + offline/index.tsx):
 *  - every queued action carries a client idempotency key and the device time it happened,
 *    created once when the action is first attempted and reused on every retry, so a
 *    replay whose first attempt actually reached the server is answered with the original
 *    result instead of being applied twice (see docs/CREW_API_CONTRACT.md "Idempotency &
 *    device time");
 *  - the queue and the cached trip belong to the signed-in user.
 */

export type QueuedActionType =
  | 'scan'
  | 'cost'
  | 'stop_advance'
  | 'crew_event'
  | 'pod'
  | 'exception'
  | 'scan_correction'
  | 'cargo_check';

export interface ReplayableItem {
  id: string;
  type: QueuedActionType;
  payload: Record<string, unknown>;
  createdAt: string;
  /** Owner (user id) of the item; absent on items queued before scoping existed. */
  userId?: string;
  /** Idempotency key sent as `Idempotency-Key` + body `clientEventId`. Defaults to `id`. */
  clientEventId?: string;
  /** When the action happened on the phone (ISO). Defaults to `createdAt`. */
  deviceTimestamp?: string;
}

/** Key + time for one crew action, created before the first (online) attempt. */
export interface CrewActionMeta {
  clientEventId: string;
  deviceTimestamp: string;
}

export function newCrewActionMeta(now: Date = new Date(), uuid: () => string = () => crypto.randomUUID()): CrewActionMeta {
  return { clientEventId: uuid(), deviceTimestamp: now.toISOString() };
}

export function replayMeta(item: Pick<ReplayableItem, 'id' | 'createdAt' | 'clientEventId' | 'deviceTimestamp'>): CrewActionMeta {
  return {
    clientEventId: item.clientEventId ?? item.id,
    deviceTimestamp: item.deviceTimestamp ?? item.createdAt,
  };
}

/**
 * Items the signed-in user may see and send. Another user's items are never returned
 * (they stay on the phone until that user signs in again). Items saved before the queue
 * was scoped (no `userId`) are treated as the current user's.
 */
export function itemsForUser<T extends { userId?: string }>(items: T[], userId: string | null | undefined): T[] {
  if (!userId) return [];
  return items.filter((i) => !i.userId || i.userId === userId);
}

/** Cache record key for per-user caches (trip, attendance). */
export function userCacheKey(userId: string | null | undefined): string {
  return userId ? `current:${userId}` : 'current:anonymous';
}

export interface ReplayRequest {
  path: string;
  body: Record<string, unknown>;
  /** Sent as the `Idempotency-Key` header. */
  idempotencyKey: string;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

/**
 * The HTTP request that replays one queued item. Every request carries the same key in the
 * `Idempotency-Key` header and body `clientEventId`, plus the device time
 * (`deviceTimestamp`, or `scannedAt` for scans). Returns null for an unknown item.
 */
export function buildReplayRequest(item: ReplayableItem): ReplayRequest | null {
  const { clientEventId, deviceTimestamp } = replayMeta(item);
  const p = item.payload ?? {};
  const meta = { clientEventId, deviceTimestamp };
  const req = (path: string, body: Record<string, unknown>): ReplayRequest => ({
    path,
    body,
    idempotencyKey: clientEventId,
  });

  switch (item.type) {
    case 'scan':
      return req('/scanning/scan', {
        tripStopId: p.stopId,
        barcode: p.barcode,
        entryMethod: str(p.entryMethod) ?? 'manual_key',
        offlineCaptured: true,
        clientEventId,
        scannedAt: deviceTimestamp,
      });
    case 'cost': {
      const { tripId, ...rest } = p;
      return req(`/trips/${tripId as string}/costs`, { ...rest, ...meta });
    }
    case 'stop_advance':
      return req(`/trips/${p.tripId as string}/stops/${p.stopId as string}/advance`, {
        eventType: p.eventType,
        offlineCaptured: true,
        ...meta,
      });
    case 'crew_event': {
      const tripId = p.tripId as string;
      const coords = (p.coords as Record<string, unknown> | undefined) ?? {};
      if (p.eventType === 'start_trip') {
        return req(`/trips/${tripId}/start`, { ...coords, offlineCaptured: true, ...meta });
      }
      if (p.eventType === 'end_trip') {
        return req(`/trips/${tripId}/end`, { ...coords, offlineCaptured: true, ...meta });
      }
      if (typeof p.stopId === 'string') {
        return req(`/trips/${tripId}/stops/${p.stopId}/advance`, {
          eventType: p.eventType,
          offlineCaptured: true,
          ...meta,
        });
      }
      return null;
    }
    case 'pod':
      return req('/pod/generate', {
        tripStopId: p.tripStopId,
        signatoryName: p.signatoryName,
        piecesDelivered: p.piecesDelivered,
        signatureImageKey: p.signatureImageKey,
        signedStatePhotoKey: p.signedStatePhotoKey,
        ...meta,
      });
    case 'exception':
      return req('/exceptions', {
        tripId: p.tripId,
        stopId: p.stopId,
        exceptionType: p.exceptionType,
        reasonCode: p.reasonCode,
        photoKeys: p.photoKeys,
        ...meta,
      });
    case 'cargo_check':
      return req(`/trips/${p.tripId as string}/stops/${p.stopId as string}/cargo-check`, {
        mode: p.mode,
        countedPieces: p.countedPieces,
        picName: p.picName,
        picConfirmed: p.picConfirmed,
        mismatchReason: p.mismatchReason,
        note: p.note,
        photo: p.photo,
        ...meta,
      });
    case 'scan_correction': {
      const scanId = p.scanId as string;
      if (p.action === 'acknowledge') {
        return req(`/scanning/scans/${scanId}/acknowledge-over`, { device: p.device, clientEventId });
      }
      return req(`/scanning/scans/${scanId}/correct-over`, {
        reason: p.reason,
        device: p.device,
        clientEventId,
      });
    }
    default:
      return null;
  }
}

/** Removes undefined fields (JSON.stringify drops them anyway; keeps tests readable). */
export function compactBody(body: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined));
}
